/**
 * NOTE: Do not add new functions to this file until we flow type it.
 * Use FLowTypedTaskStateUtils.js instead.
 */

import _ from 'lodash';
import { $none, $set, update } from 'qim';

import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import { firstItemIfArray } from 'app/common/CommonUtils';
import { isPathsGroupNode } from 'app/beta-editor/utils/PathUtils';

// !!! IMPORTANT !!!
// These functions affect the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const NODE_TYPES = {
  read: () => true,
  search: () => true,
  write: () => true,
  search_or_write: () => true,
  filter: (crumb, crumbs) => {
    const index = crumbs.indexOf(crumb);
    const nextCrumb = crumbs[index + 1] || {};
    // If `object_id`s are the *same*, we're dealing with
    // a `Rule`-based faux filter node. If they're different,
    // we're dealing with two real nodes and therefore
    // a real filter node.
    return crumb.object_id !== nextCrumb.object_id;
  },
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getNodeCrumbs = function(taskState) {
  // only grab crumbs that are node types, leaving out "filter" pseudo crumb
  return _.filter(
    taskState.breadcrumbs,
    crumb =>
      crumb.object_type &&
      _.isFunction(NODE_TYPES[crumb.object_type]) &&
      NODE_TYPES[crumb.object_type](crumb, taskState.breadcrumbs)
  );
};

const crumbsForId = (id, breadcrumbs = []) => {
  return breadcrumbs.filter(({ object_id }) => object_id === id);
};

const compareCrumbAge = (crumb1, crumb2) =>
  new Date(crumb1.start_time) - new Date(crumb2.start_time);

export const getNewestCrumbForId = (id, taskState) =>
  _.last(crumbsForId(id, taskState.breadcrumbs).sort(compareCrumbAge));

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const wasNodeRan = function(taskState, nodeId) {
  if (!taskState) {
    return false;
  }
  const breadcrumbs = getNodeCrumbs(taskState); // filters don't count as run
  return _.includes(_.map(breadcrumbs, 'object_id'), parseInt(nodeId));
};

export const wereAncestorsRan = function(taskState, ancestorIds) {
  return _.every(ancestorIds, ancestorId => {
    return wasNodeRan(taskState, ancestorId);
  });
};

const errorMap = function(taskState) {
  // Do not filter the breadcrumbs via nodeCrumbs():
  // sometimes when there is an error, the breadcrumb
  // doesn't have an object_type, it just has the error,
  // and nodeCrumbs() filters out crumbs with no object_type.
  //
  // FYI: I think the whole point of this odd method is that
  // node ids / object_ids are sometimes strings and sometimes numbers.
  // By putting the errors into a map keyed by object_id, we can
  // pluck it out via string id or number.
  const breadcrumbs = taskState.breadcrumbs;
  const keys = _.map(breadcrumbs, 'object_id');
  const values = _.map(breadcrumbs, 'error');
  return _.fromPairs(_.zip(keys, values));
};

export const getErrorForNode = function(taskState, nodeId) {
  return errorMap(taskState)[nodeId];
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Deletes all the necessary data out of the state so we are ready to hit API
// as though this node has never been run before - also strips filter tasks
export const getRollbackSample = function(taskState, nodeId) {
  if (taskState) {
    taskState = update(['output', nodeId, $set(undefined)], taskState);
    taskState = update(['statuses', nodeId, $none], taskState); // "delete"
  } else {
    taskState = {};
  }

  const newBreadCrumbs = _.filter(taskState.breadcrumbs, function(crumb) {
    return String(crumb.object_id) !== nodeId;
  });
  taskState = update(['breadcrumbs', $set(newBreadCrumbs)], taskState);

  taskState = update(['why_stop', $set({})], taskState);
  taskState = update(['stop', $set(false)], taskState);
  return taskState;
};

// similar to rollbackSample but also input
export const getRollbackNode = function(taskState, nodeId) {
  taskState = update(['input', nodeId, $set(undefined)], taskState);
  taskState = getRollbackSample(taskState, nodeId);
  return taskState;
};

/*

For some endpoints, POSTing a state where a node's output is an array
does not work because the endpoint expects to be operating on a single fork.
So, for nodes that return arrays, we arbitrarily choose the first value
(first fork) as the one we'll work on. Someday, we might let the user decide
which one to pick.

 */
export const swapListOutputsToFirstItem = taskState => {
  const newOutput = {};

  _.each(taskState.output, (nodeOutput, id) => {
    newOutput[id] = firstItemIfArray(nodeOutput) || {};
  });

  return update(['output', $set(newOutput)], taskState);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const injectError = function(errorMessage, taskState, nodeId) {
  taskState = update(['input', nodeId, $set(undefined)], taskState);
  taskState = update(['output', nodeId, $set(undefined)], taskState);
  taskState = update(['statuses', nodeId, $set('error')], taskState);

  const newBreadCrumbs = _.filter(taskState.breadcrumbs, function(crumb) {
    return String(crumb.object_id) !== nodeId;
  }).concat({
    object_id: nodeId,
    error: errorMessage,
  });
  taskState = update(['breadcrumbs', $set(newBreadCrumbs)], taskState);

  taskState = update(
    [
      'why_stop',
      $set({
        message: errorMessage,
      }),
    ],
    taskState
  );
  taskState = update(['stop', $set(true)], taskState);
  return taskState;
};

/**
 * Turns something like this:
 *  {
 *    "key": "value",
 *    "_zap_data_something": true
 *  }
 *
 * into this:
 *  {
 *    "key": "value",
 *  }
 *
 * Not recursive, as at this time we are not
 * nesting any such keys.
 */
const prefix = '_zap_data_';
const whitelist = ['was_found'].map(key => `${prefix}${key}`);
const isKeyInWhitelist = key => _.includes(whitelist, key);
const isMetaDataKey = key => key.indexOf(prefix) === 0;
const isExtraneousKey = key => isMetaDataKey(key) && !isKeyInWhitelist(key);

const removeExtraneousMetaData = data => {
  // Don't use _.reduce, because lodash thinks {length: 42} is an array.
  return _.keys(data).reduce((accum, key) => {
    if (!isExtraneousKey(key)) {
      accum[key] = data[key];
    }
    return accum;
  }, {});
};

const replaceHydrateStrings = data => {
  // Don't use _.reduce, because lodash thinks {length: 42} is an array.
  return _.keys(data).reduce((accum, key) => {
    if (_.isString(data[key]) && data[key].startsWith('hydrate|||')) {
      accum[key] = '(Exists but not shown)';
    } else {
      accum[key] = data[key];
    }
    return accum;
  }, {});
};

export const removeOrReplaceInternalData = data => {
  return replaceHydrateStrings(removeExtraneousMetaData(data));
};

export const getSelectedSample = taskState =>
  _.get(taskState, ['selected_sample']);

export const getDirtyTaskStateNodeIds = (flow, services) => {
  const dirtyTaskState = _.get(flow, ['dirtyTaskState'], {});
  const dirtyTaskStateNodeIds = Object.keys(dirtyTaskState);
  return dirtyTaskStateNodeIds.filter(nodeId => {
    const isInDirtyTaskState = dirtyTaskState[nodeId];
    const node = flow.nodes[nodeId];
    const isConfigured = NodeUtils.isConfigured(node, services);
    return !isPathsGroupNode(node) && isInDirtyTaskState && isConfigured;
  });
};

export const hasAnyWithDirtyTaskState = (flow, services) => {
  const dirtyTaskStateNodeIds = getDirtyTaskStateNodeIds(flow, services);
  return !_.isEmpty(dirtyTaskStateNodeIds);
};
